/**
 * Procedural soundscape (Web Audio): wind, rain, thunder, birds (raven, Steller's jay, mountain chickadee),
 * night owl, and surface-aware footsteps. Everything is synthesized — no audio files.
 */
export class AudioSystem {
  private ctx: AudioContext | null = null;
  private master!: GainNode;
  private windGain!: GainNode;
  private windFilter!: BiquadFilterNode;
  private gustFilter!: BiquadFilterNode;
  private gustGain!: GainNode;
  private rainGain!: GainNode;
  private rainHiss!: BiquadFilterNode;
  private noise!: AudioBuffer;
  private brown!: AudioBuffer;
  private volume = 0.7;
  private birdTimer = 3;
  private owlTimer = 20;
  private dropTimer = 0;
  enabled = true;

  unlock() {
    if (this.ctx) { void this.ctx.resume(); return; }
    const AC = window.AudioContext || (window as unknown as { webkitAudioContext: typeof AudioContext }).webkitAudioContext;
    if (!AC) return;
    const ctx = new AC();
    this.ctx = ctx;
    this.master = ctx.createGain();
    this.master.gain.value = this.volume;
    const comp = ctx.createDynamicsCompressor();
    comp.threshold.value = -14;
    comp.ratio.value = 3;
    this.master.connect(comp).connect(ctx.destination);
    // noise buffers
    const len = ctx.sampleRate * 4;
    this.noise = ctx.createBuffer(1, len, ctx.sampleRate);
    const d = this.noise.getChannelData(0);
    for (let i = 0; i < len; i++) d[i] = Math.random() * 2 - 1;
    this.brown = ctx.createBuffer(1, len, ctx.sampleRate);
    const b = this.brown.getChannelData(0);
    let last = 0;
    for (let i = 0; i < len; i++) { last = (last + 0.02 * (Math.random() * 2 - 1)) / 1.02; b[i] = last * 3.5; }
    // wind bed: brown noise -> lowpass (slowly modulated)
    const w = ctx.createBufferSource();
    w.buffer = this.brown; w.loop = true;
    this.windFilter = ctx.createBiquadFilter();
    this.windFilter.type = 'lowpass';
    this.windFilter.frequency.value = 400;
    this.windGain = ctx.createGain();
    this.windGain.gain.value = 0.0;
    w.connect(this.windFilter).connect(this.windGain).connect(this.master);
    w.start();
    // gust whistle: white noise -> bandpass
    const g = ctx.createBufferSource();
    g.buffer = this.noise; g.loop = true;
    this.gustFilter = ctx.createBiquadFilter();
    this.gustFilter.type = 'bandpass';
    this.gustFilter.Q.value = 6;
    this.gustFilter.frequency.value = 700;
    this.gustGain = ctx.createGain();
    this.gustGain.gain.value = 0;
    g.connect(this.gustFilter).connect(this.gustGain).connect(this.master);
    g.start();
    // rain hiss
    const r = ctx.createBufferSource();
    r.buffer = this.noise; r.loop = true;
    this.rainHiss = ctx.createBiquadFilter();
    this.rainHiss.type = 'bandpass';
    this.rainHiss.frequency.value = 2600;
    this.rainHiss.Q.value = 0.5;
    this.rainGain = ctx.createGain();
    this.rainGain.gain.value = 0;
    r.connect(this.rainHiss).connect(this.rainGain).connect(this.master);
    r.start();
  }

  setVolume(v: number) {
    this.volume = v;
    if (this.ctx) this.master.gain.setTargetAtTime(v, this.ctx.currentTime, 0.1);
  }

  private env(node: AudioParam, t: number, a: number, peak: number, dcy: number) {
    node.setValueAtTime(0.0001, t);
    node.exponentialRampToValueAtTime(peak, t + a);
    node.exponentialRampToValueAtTime(0.0001, t + a + dcy);
  }

  footstep(surface: 'trail' | 'rock' | 'ground' | 'snow' | 'paved', intensity: number) {
    const ctx = this.ctx;
    if (!ctx || !this.enabled) return;
    const t = ctx.currentTime + 0.01;
    // heel + toe crunch: several short filtered noise grains
    const grains = surface === 'paved' ? 2 : surface === 'snow' ? 7 : 5;
    for (let i = 0; i < grains; i++) {
      const src = ctx.createBufferSource();
      src.buffer = this.noise;
      const f = ctx.createBiquadFilter();
      f.type = 'bandpass';
      f.frequency.value = surface === 'snow' ? 900 + Math.random() * 700 : surface === 'paved' ? 1800 : 2200 + Math.random() * 2600;
      f.Q.value = surface === 'snow' ? 1.2 : 2.5;
      const g = ctx.createGain();
      const at = t + i * (surface === 'snow' ? 0.018 : 0.012) + Math.random() * 0.01;
      this.env(g.gain, at, 0.002, 0.25 * intensity * (surface === 'paved' ? 0.6 : 1), surface === 'snow' ? 0.07 : 0.035);
      src.connect(f).connect(g).connect(this.master);
      src.start(at, Math.random() * 3, 0.15);
    }
    // body thump
    const o = ctx.createOscillator();
    o.frequency.setValueAtTime(110, t);
    o.frequency.exponentialRampToValueAtTime(55, t + 0.08);
    const og = ctx.createGain();
    this.env(og.gain, t, 0.004, 0.12 * intensity, 0.09);
    o.connect(og).connect(this.master);
    o.start(t); o.stop(t + 0.15);
  }

  thunder(delay: number, intensity: number) {
    const ctx = this.ctx;
    if (!ctx) return;
    const t = ctx.currentTime + delay;
    const src = ctx.createBufferSource();
    src.buffer = this.brown;
    const f = ctx.createBiquadFilter();
    f.type = 'lowpass';
    f.frequency.setValueAtTime(900 * intensity + 200, t);
    f.frequency.exponentialRampToValueAtTime(120, t + 5);
    const g = ctx.createGain();
    g.gain.setValueAtTime(0.0001, t);
    g.gain.exponentialRampToValueAtTime(1.4 * intensity + 0.2, t + 0.08 + (1 - intensity) * 0.4);
    // rolling rumble
    for (let k = 1; k < 6; k++) g.gain.setTargetAtTime((0.7 + Math.random() * 0.5) * intensity * (1 - k / 7), t + k * 0.55, 0.25);
    g.gain.setTargetAtTime(0.0001, t + 4.2, 1.2);
    src.connect(f).connect(g).connect(this.master);
    src.start(t, Math.random() * 2, 9);
  }

  private chirp(t: number, f0: number, f1: number, dur: number, gain: number, type: OscillatorType = 'sine', fm = 0) {
    const ctx = this.ctx!;
    const o = ctx.createOscillator();
    o.type = type;
    o.frequency.setValueAtTime(f0, t);
    o.frequency.exponentialRampToValueAtTime(f1, t + dur);
    if (fm > 0) {
      const m = ctx.createOscillator();
      m.frequency.value = fm;
      const mg = ctx.createGain();
      mg.gain.value = f0 * 0.08;
      m.connect(mg).connect(o.frequency);
      m.start(t); m.stop(t + dur + 0.05);
    }
    const g = ctx.createGain();
    g.gain.setValueAtTime(0.0001, t);
    g.gain.exponentialRampToValueAtTime(gain, t + Math.min(0.02, dur * 0.3));
    g.gain.exponentialRampToValueAtTime(0.0001, t + dur);
    const pan = ctx.createStereoPanner();
    pan.pan.value = Math.random() * 1.6 - 0.8;
    const bp = ctx.createBiquadFilter();
    bp.type = 'highpass';
    bp.frequency.value = 300;
    o.connect(g).connect(bp).connect(pan).connect(this.master);
    o.start(t); o.stop(t + dur + 0.05);
  }

  private bird() {
    const ctx = this.ctx!;
    const t = ctx.currentTime + 0.05;
    const r = Math.random();
    const dist = 0.3 + Math.random() * 0.7;
    if (r < 0.35) {
      // common raven: hoarse "kraa" croaks
      const n = 1 + Math.floor(Math.random() * 3);
      for (let i = 0; i < n; i++) this.chirp(t + i * 0.42, 420, 330, 0.28, 0.05 * dist, 'sawtooth', 38);
    } else if (r < 0.65) {
      // Steller's jay: harsh "shook-shook-shook"
      const n = 3 + Math.floor(Math.random() * 3);
      for (let i = 0; i < n; i++) this.chirp(t + i * 0.19, 2300, 1500, 0.11, 0.035 * dist, 'square', 90);
    } else {
      // mountain chickadee: "fee-bee-bee"
      this.chirp(t, 3900, 3700, 0.32, 0.03 * dist);
      this.chirp(t + 0.4, 3300, 3200, 0.2, 0.026 * dist);
      this.chirp(t + 0.65, 3300, 3150, 0.2, 0.024 * dist);
    }
  }

  private owl() {
    const t = this.ctx!.currentTime + 0.05;
    // great horned owl: "hoo h'hoo hoo hoo"
    const pat = [0, 0.55, 0.75, 1.35, 1.95];
    for (const p of pat) this.chirp(t + p, 330, 300, 0.32, 0.03);
  }

  update(dt: number, s: { wind: number; rain: number; snow: number; flySpeed: number; altitudeAGL: number; sunAlt: number; precipQuiet: boolean }) {
    const ctx = this.ctx;
    if (!ctx || ctx.state !== 'running') return;
    const now = ctx.currentTime;
    const exposure = Math.min(1, 0.35 + s.altitudeAGL / 200);
    const windAmt = Math.min(1.2, (s.wind / 14) * exposure + s.flySpeed / 160 + 0.06);
    const gust = 0.6 + 0.4 * Math.sin(now * 0.37) * Math.sin(now * 0.13 + 1.3);
    this.windGain.gain.setTargetAtTime(0.18 * windAmt * gust, now, 0.4);
    this.windFilter.frequency.setTargetAtTime(220 + 900 * windAmt * gust, now, 0.5);
    this.gustGain.gain.setTargetAtTime(0.012 * Math.max(0, windAmt - 0.35) * gust, now, 0.6);
    this.gustFilter.frequency.setTargetAtTime(500 + 900 * gust + s.flySpeed * 4, now, 0.8);
    const rainAmt = s.rain;
    this.rainGain.gain.setTargetAtTime(0.16 * rainAmt + 0.02 * s.snow, now, 0.6);
    this.rainHiss.frequency.setTargetAtTime(s.snow > s.rain ? 1200 : 2600, now, 1);
    // individual drops
    if (rainAmt > 0.05) {
      this.dropTimer -= dt;
      while (this.dropTimer < 0) {
        this.dropTimer += 0.02 / rainAmt;
        const src = ctx.createBufferSource();
        src.buffer = this.noise;
        const f = ctx.createBiquadFilter();
        f.type = 'bandpass'; f.frequency.value = 2500 + Math.random() * 5000; f.Q.value = 8;
        const g = ctx.createGain();
        this.env(g.gain, now + Math.random() * 0.02, 0.001, 0.05 * Math.random() * rainAmt, 0.02);
        src.connect(f).connect(g).connect(this.master);
        src.start(now, Math.random() * 3, 0.05);
      }
    }
    // wildlife
    const day = s.sunAlt > -2;
    const calm = s.rain < 0.1 && s.snow < 0.2 && s.wind < 10 && s.flySpeed < 30;
    this.birdTimer -= dt;
    if (this.birdTimer <= 0) {
      this.birdTimer = 4 + Math.random() * 12;
      if (day && calm) this.bird();
    }
    this.owlTimer -= dt;
    if (this.owlTimer <= 0) {
      this.owlTimer = 25 + Math.random() * 40;
      if (!day && calm) this.owl();
    }
  }
}
